import React from 'react';
import { CheckCircle, Home, Sparkles, CalendarDays, ShirtIcon, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ServiceHero from '../components/ServiceHero';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

const InteriorCleaning = () => {
  const scrollToQuote = () => {
    const element = document.querySelector('#quote-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const maintenanceItems = [
    "Houses & apartments",
    "Storefronts & offices",
    "Weekly service",
    "Biweekly service",
    "As-needed scheduling",
    "Custom maintenance plans"
  ];

  const deepCleaningItems = [
    "Summer season opening & closing",
    "Memorial Day through Labor Day rental turnovers",
    "Pre-rental deep cleaning",
    "Post-rental deep cleaning",
    "Move-in / move-out cleaning",
    "Full property sanitization"
  ];

  const organizingItems = [
    "Detailed closet organization",
    "Garage cleanout & organization",
    "Basement decluttering & organizing",
    "Pantry & kitchen organization",
    "Custom storage solutions"
  ];

  const laundryItems = [
    "Wash & press linens and towels",
    "Using your machine and supplies",
    "Personal clothing upon request",
    "Bed making with fresh linens",
    "Folding & organized placement"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <ServiceHero
        title="Interior Cleaning"
        headline="Messy Inside?"
        description="Professional interior cleaning for homes, rentals, and commercial spaces throughout the Hamptons and Long Island."
        backgroundImage="https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/a38e0d4cd0acb762b3def89e2217e57063fe3da2f39228bd6e57099565feceb7.png"
        defaultService="Interior Cleaning"
      />

      {/* Intro Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Your home deserves the same level of care inside as it gets outside.{' '}
            <span className="text-gradient-reflections font-bold">Reflections Cleanings</span>{' '}
            offers a full suite of interior cleaning services — from routine maintenance to
            seasonal deep cleans, home organizing, and laundry services.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you're preparing a Hamptons rental for the summer season, keeping your
            home spotless year-round, or tackling a major organizing project, our team delivers
            reliable, thorough results every time.
          </p>
        </div>
      </section>

      {/* Maintenance Cleaning Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <Home className="w-7 h-7 text-blue-600" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-blue-600" data-testid="maintenance-cleaning-heading">
                  Maintenance Cleaning
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Houses, apartments, storefronts, offices — we keep your spaces clean and
                welcoming on a schedule that works for you. Choose from weekly, biweekly,
                or as-needed service plans.
              </p>
              <ul className="space-y-3 mb-8">
                {maintenanceItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                data-testid="maintenance-cta"
              >
                Get a Maintenance Quote
              </button>
            </div>

            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/a38e0d4cd0acb762b3def89e2217e57063fe3da2f39228bd6e57099565feceb7.png"
                alt="Professional maintenance cleaning service"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-4 -left-4 bg-blue-600 text-white px-6 py-4 rounded-2xl shadow-lg">
                <div className="flex items-center gap-3">
                  <Home className="w-8 h-8" />
                  <div>
                    <div className="text-xl font-bold">Flexible</div>
                    <div className="text-sm text-blue-100">Scheduling</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seasonal & Rental Deep Cleaning Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="order-2 lg:order-1 relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/e8d22489ebb479c8d8134d93ff5acdbbefde968416e717a65f65e50c8bf0bd21.png"
                alt="Seasonal deep cleaning for rental properties"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
              <div className="absolute -top-4 -right-4 bg-white text-gray-900 px-6 py-4 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="text-lg font-bold text-blue-600">Seasonal</div>
                    <div className="text-sm text-gray-600">Deep Cleans</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <CalendarDays className="w-7 h-7 text-blue-600" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-blue-600" data-testid="deep-cleaning-heading">
                  Seasonal & Rental Deep Cleaning
                </h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Deep cleaning to open and close for summer season, and Memorial Day
                through Labor Day pre and post rentals. We ensure every property is
                guest-ready and sparkling.
              </p>
              <ul className="space-y-3 mb-8">
                {deepCleaningItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                data-testid="deep-cleaning-cta"
              >
                Book a Deep Clean
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Home Organizing & Laundry - Two Column */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Home Organizing */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <Sparkles className="w-7 h-7 text-blue-600" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-blue-600" data-testid="organizing-heading">
                  Home Organizing Service
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                Detailed closet, garage, and basement projects. We transform cluttered
                spaces into organized, functional areas you'll love.
              </p>
              <ul className="space-y-3 mb-6">
                {organizingItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:scale-105 shadow-lg"
                data-testid="organizing-cta"
              >
                Get Organized
              </button>
            </div>

            {/* Laundry Services */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <ShirtIcon className="w-7 h-7 text-blue-600" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-blue-600" data-testid="laundry-heading">
                  Laundry Services
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                We wash and press linens and towels with your machine and supplies.
                Personal clothing laundered upon request.
              </p>
              <ul className="space-y-3 mb-6">
                {laundryItems.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToQuote}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:scale-105 shadow-lg"
                data-testid="laundry-cta"
              >
                Request Laundry Service
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="why-interior-heading">
            Why Choose <span className="text-gradient-reflections">Reflections Cleanings</span> for Interior Services
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            The same attention to detail and professionalism we bring to every exterior
            project carries over to every room inside your home.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Trusted throughout the Hamptons & Long Island",
              "Fully insured & background-checked team",
              "Flexible scheduling options",
              "Rental turnover specialists",
              "Discreet & respectful service",
              "100% satisfaction guarantee"
            ].map((item, index) => (
              <div key={index} className="bg-blue-800/50 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-blue-300 flex-shrink-0" />
                <span className="text-blue-100">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section id="quote-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="interior-quote-heading">
            Request an Interior Cleaning Quote
          </h2>
          <p className="text-lg text-gray-600 mb-2">
            Whether it's weekly maintenance, a seasonal deep clean, or a full organizing
            project — we're ready to help.
          </p>
          <p className="text-gray-600 mb-8">
            Get a detailed quote within 1 business day.
          </p>
          <Link
            to="/#quote"
            data-testid="interior-quote-link"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Get Your Free Quote
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default InteriorCleaning;
